import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, Paper, Chip, IconButton, Checkbox, CircularProgress,
} from '@mui/material';
import { Delete as DeleteIcon, GetApp as DownloadIcon, AttachFile as AttachFileIcon } from '@mui/icons-material';
import { getFileIcon, formatFileSize } from '../../../utils/projectViewUtils';

const AttachmentsDialog = ({
  open, onClose, selectedItem, attachments, uploadingFile,
  fileVisibleToHomeowner, onFileVisibleChange, onFileUpload,
  onDeleteAttachment, onDownloadAttachment, user,
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle>Attachments: {selectedItem?.title}</DialogTitle>
    <DialogContent>
      {attachments.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
          No files attached yet. Upload a file to get started!
        </Typography>
      ) : (
        <Box sx={{ mb: 3 }}>
          {attachments.map((attachment) => (
            <Paper key={attachment.id} variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box display="flex" alignItems="center" gap={2} flex={1}>
                  {getFileIcon(attachment.file_type)}
                  <Box flex={1}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {attachment.original_filename}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                      <Chip
                        label={`${attachment.first_name} ${attachment.last_name}`}
                        size="small"
                        color={
                          attachment.role === 'admin' ? 'error' :
                          attachment.role === 'contractor' ? 'primary' :
                          'secondary'
                        }
                      />
                      <Typography variant="caption" color="text.secondary">
                        {formatFileSize(attachment.file_size)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(attachment.created_at).toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box display="flex" gap={1}>
                  <IconButton size="small" color="primary" onClick={() => onDownloadAttachment(attachment)} title="Download" aria-label={`Download ${attachment.file_name || 'attachment'}`}>
                    <DownloadIcon />
                  </IconButton>
                  {(attachment.user_id === user.id || user.role === 'admin') && (
                    <IconButton size="small" color="error" onClick={() => onDeleteAttachment(attachment.id)} title="Delete" aria-label={`Delete ${attachment.file_name || 'attachment'}`}>
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>
      )}

      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" gutterBottom>Upload File</Typography>
        {user.role === 'contractor' && (
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <Checkbox
              checked={fileVisibleToHomeowner}
              onChange={(e) => onFileVisibleChange(e.target.checked)}
            />
            <Typography variant="body2">Visible to Homeowner</Typography>
          </Box>
        )}
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            variant="contained"
            component="label"
            startIcon={<AttachFileIcon />}
            disabled={uploadingFile}
            sx={{ textTransform: 'none' }}
          >
            {uploadingFile ? 'Uploading...' : 'Choose File'}
            <input
              type="file"
              hidden
              onChange={onFileUpload}
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
              capture="environment"
            />
          </Button>
          {uploadingFile && <CircularProgress size={24} />}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Accepted formats: Images, PDFs, Documents, Spreadsheets (Max 10MB)
        </Typography>
      </Box>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Close</Button>
    </DialogActions>
  </Dialog>
);

export default AttachmentsDialog;
